import { useState } from 'react';
import Styles from './APIEnvironmentsTable.module.scss';
import {
    Table, TableBody, TableCell, TextField,
    TableContainer, TableHead, TableRow, Paper,
    Select, MenuItem, IconButton, Button, Tooltip
} from '@mui/material';

import DeleteForeverIcon from '@mui/icons-material/DeleteForever';
import DeleteIcon from '@mui/icons-material/Delete';
import { listKeyGenerator } from 'utils/helpers/listKeyGenerator';
import { useTranslation } from 'components/lib';

export function APIEnvironmentsTable({ envVars, setEnvironments, environments, currentEnv, setCurrentEnv, isDisabled }) {

    const [envListKeys, setEnvListKeys] = useState([]);
    const [newKey, setNewKey] = useState('');
    const [newValue, setNewValue] = useState('');

    const { t } = useTranslation();

    function createData(key, value) {
        return { key, value };
    }

    const inputChange = (changeFunc, value) => {
        changeFunc(value);
    };

    const onAddEnvVar = () => {
        if (newKey.length > 0 && newValue.length > 0) {
            setEnvListKeys(prevList => [...prevList, listKeyGenerator(1)[0]]);
            setEnvironments(prev => {
                const updated = { ...prev };
                updated[currentEnv] = [...updated[currentEnv], createData(newKey, newValue)];
                return updated;
            });
            setNewKey('');
            setNewValue('');
        }
    };

    const updateRows = (index, key, value) => {
        setEnvironments(prev => {
            const updated = { ...prev };
            updated[currentEnv][index] = { ...updated[currentEnv][index], [key]: value };
            return updated;
        });
    };

    const deleteRow = (index) => {
        setEnvironments(prev => {
            const updated = { ...prev };
            updated[currentEnv] = updated[currentEnv].filter((_, i) => i !== index);
            return updated;
        });
        setEnvListKeys(prevList => {
            const newList = [...prevList];
            newList.splice(index, 1);
            return newList;
        });
    };

    const clearAllRows = () => {
        setEnvironments(prev => {
            const updated = { ...prev };
            updated[currentEnv] = [];
            return updated;
        });
        setEnvListKeys([]);
    };
    return (
        <div id='envVarsParent' style={{ height: '100%', width: '100%' }}>
            {/* Environment Selector */}
            <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: "10px" }}>
                <Select
                    value={currentEnv}
                    onChange={(e) => setCurrentEnv(e.target.value)}
                    size="small"
                >
                    <MenuItem value="prod">{t('Components.NodeLayout.NodeTopostman.env.environments.prod')}</MenuItem>
                    <MenuItem value="qa">{t('Components.NodeLayout.NodeTopostman.env.environments.qa')}</MenuItem>
                    <MenuItem value="dev">{t('Components.NodeLayout.NodeTopostman.env.environments.dev')}</MenuItem>
                </Select>
            </div>

            {/* Table */}
            <TableContainer component={Paper}>
                <Table
                    sx={{ minWidth: 650 }}
                    aria-label={t('Components.NodeLayout.NodeTopostman.env.aria.table')}
                    size="small"
                >
                    <TableHead>
                        <TableRow>
                            <TableCell sx={{ borderRight: "1px solid lightgray;", fontWeight: 600, width: '50%' }}>
                                {t('Components.NodeLayout.NodeTopostman.env.table.key')}
                            </TableCell>
                            <TableCell
                                sx={{ fontWeight: 600, width: '50%' }}
                            >
                                {t('Components.NodeLayout.NodeTopostman.env.table.value')}
                            </TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {environments[currentEnv].map((row, index) => (
                            <TableRow key={envListKeys[index] || index}>
                                <TableCell
                                    component="th"
                                    scope="row"
                                    sx={{
                                        borderRight: "1px solid lightgray;",
                                        bgcolor: "#f1f8e9",
                                        paddingLeft: "14px"
                                    }}
                                >
                                    <input
                                        type="text"
                                        value={row.key}
                                        className={Styles.inputBoxes}
                                        onChange={(e) => updateRows(index, 'key', e.target.value)}
                                    />
                                </TableCell>
                                <TableCell
                                    scope="row"
                                    sx={{
                                        bgcolor: "#f1f8e9",
                                        paddingLeft: "14px"
                                    }}
                                >
                                    <div style={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'space-between',
                                        gap: '8px'
                                    }}>
                                        <input
                                            type="text"
                                            value={row.value}
                                            className={Styles.inputBoxes}
                                            onChange={(e) => updateRows(index, 'value', e.target.value)}
                                            style={{ flex: 1 }}
                                        />
                                        <IconButton
                                            size="small"
                                            onClick={() => deleteRow(index)}
                                            sx={{
                                                color: '#d32f2f',
                                                padding: '4px',
                                                '&:hover': {
                                                    backgroundColor: 'rgba(211, 47, 47, 0.08)'
                                                }
                                            }}
                                            title={t('Components.NodeLayout.NodeTopostman.env.delete') || 'Delete row'}
                                        >
                                            <DeleteIcon fontSize="small" />
                                        </IconButton>
                                    </div>
                                </TableCell>
                            </TableRow>
                        ))}
                    </TableBody>
                </Table>
            </TableContainer>

            {/* Inputs for new env var */}
            <div id='addEnvInputs' className={Styles.addEnvInputs}>
                <TextField
                    disabled={isDisabled}
                    placeholder={t('Components.NodeLayout.NodeTopostman.env.placeholder.key')}
                    sx={{ width: '100%' }}
                    value={newKey}
                    size="small"
                    onChange={(e) => inputChange(setNewKey, e.target.value)}
                    inputProps={{ maxLength: 50 }}
                />
                <TextField
                    disabled={isDisabled}
                    placeholder={t('Components.NodeLayout.NodeTopostman.env.placeholder.value')}
                    sx={{ width: '100%' }}
                    value={newValue}
                    size="small"
                    onChange={(e) => inputChange(setNewValue, e.target.value)}
                    inputProps={{ maxLength: 500 }}
                />
            </div>

            {/* Buttons */}
            <div id='addEnvBtnContainer' className={Styles.addEnvBtnContainer}>
                <Tooltip title={t('Components.NodeLayout.NodeTopostman.env.tooltip')}>
                    <span>
                        <Button
                            id="resetEnvBtn"
                            variant="outlined"
                            color="error"
                            disabled={environments[currentEnv].length === 0}
                            onClick={clearAllRows}
                            startIcon={<DeleteForeverIcon />}
                            sx={{
                                textTransform: 'none',
                                fontSize: '0.8125rem',
                                fontWeight: 500,
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                gap: '4px',
                                padding: '4px 10px',
                                borderColor: '#d32f2f',
                                color: '#d32f2f',
                                '&:hover': {
                                    backgroundColor: 'rgba(211, 47, 47, 0.08)',
                                    borderColor: '#d32f2f',
                                },
                                '&.Mui-disabled': {
                                    color: 'rgba(0,0,0,0.26)',
                                    borderColor: 'rgba(0,0,0,0.12)',
                                },
                            }}
                        >
                            {t('Components.NodeLayout.NodeTopostman.env.clearAllBtn')}
                        </Button>
                    </span>
                </Tooltip>
                <button
                    id='addEnvBtn'
                    className={Styles.addEnvBtn}
                    onClick={() => { onAddEnvVar() }}
                    disabled={newKey.length === 0 || newValue.length === 0}
                >
                    {t('Components.NodeLayout.NodeTopostman.env.button')}
                </button>
            </div>
        </div>
    );
}